/**
 * Live catalogue: seed PRODUCTS + optional Supabase products + per-user local seller rows.
 * Buyers see active items; sellers manage their factory's rows (company match).
 */
import type { PriceTier, Product } from '../types';
import { PRODUCTS } from '../data/products';
import { USD_TO_NGN } from './currency';
import { authState } from './auth';
import { supabase, isSupabaseConfigured } from './supabase';

const LOCAL_SELLER_KEY = 'forgelink_seller_products';
const GUEST_SCOPE = 'guest';
export const PRODUCT_IMAGE_BUCKET = 'product-images';

export type CatalogProduct = Product & {
  active: boolean;
  /** True when row came from seller localStorage (offline / pre-migration). */
  localOnly?: boolean;
};

export type ProductDraft = {
  id?: string;
  name: string;
  moq: number;
  unitPriceNgn: number;
  category: string;
  description: string;
  active: boolean;
  /** Existing public URL when not replacing the file. */
  imageUrl?: string | null;
  unit?: string;
  sku?: string;
};

type DbProductRow = {
  id: string;
  factory_id?: string | null;
  factory_name: string;
  name: string;
  sku: string;
  category: string;
  description?: string | null;
  specs?: Record<string, unknown> | null;
  moq: number;
  unit: string;
  unit_price: number;
  price_tiers?: PriceTier[] | null;
  lead_time_days: number;
  icon?: string | null;
  featured?: boolean | null;
  active?: boolean | null;
  image_url?: string | null;
};

let catalog: CatalogProduct[] = seedAsCatalog();
let catalogVersion = 0;
const listeners = new Set<() => void>();

function seedAsCatalog(): CatalogProduct[] {
  return PRODUCTS.map((p) => ({ ...p, active: true }));
}

function storageScope(): string {
  return authState.user?.id ? String(authState.user.id) : GUEST_SCOPE;
}

function localKey(): string {
  return `${LOCAL_SELLER_KEY}:${storageScope()}`;
}

function readLocalSellerProducts(): CatalogProduct[] {
  try {
    const raw = localStorage.getItem(localKey());
    if (!raw) return [];
    const list = JSON.parse(raw) as CatalogProduct[];
    return Array.isArray(list) ? list.map((p) => ({ ...p, localOnly: true, active: p.active !== false })) : [];
  } catch {
    return [];
  }
}

function writeLocalSellerProducts(list: CatalogProduct[]): void {
  localStorage.setItem(localKey(), JSON.stringify(list));
}

function notify(): void {
  catalogVersion += 1;
  for (const fn of listeners) {
    try {
      fn();
    } catch (err) {
      console.warn('catalog listener error', err);
    }
  }
}

export function onCatalogChange(fn: () => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

export function getCatalogVersion(): number {
  return catalogVersion;
}

export function getCatalog(): CatalogProduct[] {
  return catalog;
}

export function getActiveCatalog(): CatalogProduct[] {
  return catalog.filter((p) => p.active !== false);
}

export function productById(id: string | null | undefined): CatalogProduct | undefined {
  if (!id) return undefined;
  return catalog.find((p) => p.id === id);
}

function asNumber(value: unknown, fallback = 0): number {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function tiersFromUnknown(value: unknown): PriceTier[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((t) => {
      if (!t || typeof t !== 'object') return null;
      const row = t as Record<string, unknown>;
      const minQty = asNumber(row.minQty ?? row.min_qty, 0);
      const price = asNumber(row.price, 0);
      if (minQty <= 0) return null;
      return { minQty, price };
    })
    .filter((t): t is PriceTier => Boolean(t));
}

function specsRecord(specs: Record<string, unknown> | null | undefined): Record<string, string> {
  const out: Record<string, string> = {};
  if (!specs || typeof specs !== 'object') return out;
  for (const [k, v] of Object.entries(specs)) {
    if (k === 'unit_price_ngn' || k === 'price_tiers_ngn' || k === 'usd_to_ngn_rate' || k === 'currency_display') {
      continue;
    }
    if (v == null) continue;
    if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') {
      out[k] = String(v);
    }
  }
  return out;
}

export function mapDbProduct(row: DbProductRow): CatalogProduct {
  const specsRaw = (row.specs || {}) as Record<string, unknown>;
  const unitPriceNgn =
    typeof specsRaw.unit_price_ngn === 'number'
      ? specsRaw.unit_price_ngn
      : asNumber(specsRaw.unit_price_ngn, NaN);
  const priceTiersNgn = tiersFromUnknown(specsRaw.price_tiers_ngn);
  const priceTiers = tiersFromUnknown(row.price_tiers) || [];
  return {
    id: row.id,
    name: row.name,
    sku: row.sku,
    factory: row.factory_name,
    category: row.category,
    description: row.description || '',
    specs: specsRecord(specsRaw),
    moq: asNumber(row.moq, 1),
    unit: row.unit || 'units',
    unitPrice: asNumber(row.unit_price, 0),
    unitPriceNgn: Number.isFinite(unitPriceNgn) ? unitPriceNgn : undefined,
    priceTiers,
    priceTiersNgn: priceTiersNgn.length ? priceTiersNgn : undefined,
    leadTimeDays: asNumber(row.lead_time_days, 14),
    icon: row.icon || '📦',
    featured: Boolean(row.featured),
    active: row.active !== false,
    imageUrl: row.image_url || null,
  };
}

function mergeCatalog(remote: CatalogProduct[], localSeller: CatalogProduct[]): CatalogProduct[] {
  const byId = new Map<string, CatalogProduct>();
  for (const p of seedAsCatalog()) byId.set(p.id, p);
  for (const p of remote) byId.set(p.id, { ...byId.get(p.id), ...p, localOnly: false });
  for (const p of localSeller) {
    const existing = byId.get(p.id);
    // Prefer remote if present; keep local overlay for offline-created ids.
    if (!existing || existing.localOnly || !remote.some((r) => r.id === p.id)) {
      byId.set(p.id, { ...existing, ...p, localOnly: true });
    }
  }
  return [...byId.values()];
}

/** Rebuild catalogue from seed + Supabase active/all factory rows + local seller cache. */
export async function refreshCatalog(opts: { includeInactiveForFactory?: string | null } = {}): Promise<CatalogProduct[]> {
  const localSeller = readLocalSellerProducts();
  let remote: CatalogProduct[] = [];

  if (isSupabaseConfigured && supabase) {
    try {
      const factory = opts.includeInactiveForFactory?.trim();
      const { data: activeRows, error: activeErr } = await supabase
        .from('products')
        .select('*')
        .eq('active', true);
      if (activeErr) throw activeErr;
      remote = (activeRows || []).map((r) => mapDbProduct(r as DbProductRow));

      if (factory) {
        const { data: ownRows, error: ownErr } = await supabase
          .from('products')
          .select('*')
          .eq('factory_name', factory);
        if (!ownErr && ownRows) {
          const own = ownRows.map((r) => mapDbProduct(r as DbProductRow));
          const byId = new Map(remote.map((p) => [p.id, p]));
          for (const p of own) byId.set(p.id, p);
          remote = [...byId.values()];
        }
      }
    } catch (err) {
      console.warn('catalogue refresh from Supabase skipped:', err);
    }
  }

  catalog = mergeCatalog(remote, localSeller);
  notify();
  return catalog;
}

export function sellerFactoryName(): string | null {
  if (authState.profile?.role === 'factory' && authState.profile.company) {
    return authState.profile.company.trim() || null;
  }
  const meta = authState.user?.user_metadata?.company;
  return typeof meta === 'string' && meta.trim() ? meta.trim() : null;
}

export function listSellerProducts(): CatalogProduct[] {
  const factory = sellerFactoryName();
  if (!factory) {
    // Demo: show local seller products for current scope when no company set.
    return readLocalSellerProducts();
  }
  return catalog.filter((p) => p.factory === factory);
}

function slugify(input: string): string {
  return input
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 48) || 'item';
}

function newProductId(name: string, factory: string): string {
  const base = `sc-${slugify(factory).slice(0, 12)}-${slugify(name)}`;
  const suffix = Date.now().toString(36).slice(-4);
  return `${base}-${suffix}`.slice(0, 80);
}

function newSku(name: string): string {
  const code = slugify(name).replace(/-/g, '').slice(0, 8).toUpperCase() || 'ITEM';
  return `SC-${code}-${Date.now().toString(36).slice(-3).toUpperCase()}`;
}

function buildSpecs(unitPriceNgn: number, descriptionHints?: Record<string, string>): Record<string, unknown> {
  return {
    ...(descriptionHints || {}),
    currency_display: 'NGN',
    usd_to_ngn_rate: USD_TO_NGN,
    unit_price_ngn: unitPriceNgn,
    price_tiers_ngn: [{ minQty: 1, price: unitPriceNgn }],
  };
}

function draftToProduct(draft: ProductDraft, factory: string, existing?: CatalogProduct): CatalogProduct {
  const moq = Math.max(1, Math.floor(draft.moq) || 1);
  const unitPriceNgn = Math.max(0, Math.round(draft.unitPriceNgn) || 0);
  const unitPrice = unitPriceNgn / USD_TO_NGN;
  const id = draft.id || existing?.id || newProductId(draft.name, factory);
  const sku = draft.sku || existing?.sku || newSku(draft.name);
  return {
    id,
    name: draft.name.trim(),
    sku,
    factory,
    category: draft.category,
    description: draft.description.trim(),
    specs: existing?.specs || {},
    moq,
    unit: draft.unit || existing?.unit || 'units',
    unitPrice,
    unitPriceNgn,
    priceTiers: [{ minQty: moq, price: unitPrice }],
    priceTiersNgn: [{ minQty: moq, price: unitPriceNgn }],
    leadTimeDays: existing?.leadTimeDays ?? 14,
    icon: existing?.icon || '📦',
    featured: existing?.featured ?? false,
    active: draft.active !== false,
    imageUrl: draft.imageUrl !== undefined ? draft.imageUrl : existing?.imageUrl ?? null,
  };
}

function toDbPayload(product: CatalogProduct): Record<string, unknown> {
  const unitPriceNgn = product.unitPriceNgn ?? Math.round(product.unitPrice * USD_TO_NGN);
  return {
    id: product.id,
    factory_name: product.factory,
    name: product.name,
    sku: product.sku,
    category: product.category,
    description: product.description,
    specs: buildSpecs(unitPriceNgn, product.specs),
    moq: product.moq,
    unit: product.unit,
    unit_price: product.unitPrice,
    price_tiers: product.priceTiers,
    lead_time_days: product.leadTimeDays,
    icon: product.icon,
    featured: product.featured,
    active: product.active !== false,
    image_url: product.imageUrl || null,
  };
}

function upsertLocal(product: CatalogProduct): void {
  const list = readLocalSellerProducts().filter((p) => p.id !== product.id);
  list.unshift({ ...product, localOnly: true });
  writeLocalSellerProducts(list);
}

/**
 * Upload an image file to Storage bucket product-images.
 * Returns public URL. Requires migration 007 + factory session.
 */
export async function uploadProductImage(productId: string, file: File): Promise<string> {
  if (!isSupabaseConfigured || !supabase) {
    throw new Error('Supabase is not configured — cannot upload images');
  }
  if (!authState.user) {
    throw new Error('Sign in as a factory seller to upload images');
  }
  if (!file.type.startsWith('image/')) {
    throw new Error('Please choose an image file (JPEG, PNG, WebP, or GIF)');
  }
  if (file.size > 5 * 1024 * 1024) {
    throw new Error('Image must be 5 MB or smaller');
  }
  const ext =
    file.type === 'image/png'
      ? 'png'
      : file.type === 'image/webp'
        ? 'webp'
        : file.type === 'image/gif'
          ? 'gif'
          : 'jpg';
  const path = `${authState.user.id}/${productId}-${Date.now()}.${ext}`;
  const { error } = await supabase.storage.from(PRODUCT_IMAGE_BUCKET).upload(path, file, {
    cacheControl: '3600',
    upsert: true,
    contentType: file.type,
  });
  if (error) {
    throw new Error(
      error.message.includes('Bucket not found') || error.message.includes('not found')
        ? 'Storage bucket product-images is missing — apply migration 007 in the Supabase SQL Editor'
        : error.message,
    );
  }
  const { data } = supabase.storage.from(PRODUCT_IMAGE_BUCKET).getPublicUrl(path);
  if (!data?.publicUrl) throw new Error('Could not resolve public image URL');
  return data.publicUrl;
}

/** Create or update a seller catalogue product (Supabase when available, else localStorage). */
export async function saveSellerProduct(
  draft: ProductDraft,
  file?: File | null,
): Promise<CatalogProduct> {
  const factory = sellerFactoryName();
  if (!factory) {
    throw new Error('Set your factory company on your profile (Sign up as Factory with a company name)');
  }
  const existing = draft.id ? productById(draft.id) : undefined;
  if (existing && existing.factory !== factory) {
    throw new Error('You can only edit products for your own factory');
  }

  let product = draftToProduct(draft, factory, existing);

  let imageWarning: string | null = null;
  if (file) {
    try {
      const url = await uploadProductImage(product.id, file);
      product = { ...product, imageUrl: url };
    } catch (err) {
      console.warn('image upload failed; saving product without new image:', err);
      imageWarning =
        err instanceof Error
          ? err.message
          : 'Image upload failed — apply migration 007 (Storage bucket product-images).';
    }
  }

  upsertLocal(product);

  let remoteWarning: string | null = null;
  if (isSupabaseConfigured && supabase) {
    const payload = toDbPayload(product);
    const { data, error } = await supabase
      .from('products')
      .upsert(payload, { onConflict: 'id' })
      .select('*')
      .maybeSingle();
    if (error) {
      // Keep local copy so Seller Desk still works before RLS/migration 007.
      console.warn('Supabase product upsert failed (apply migration 007?):', error.message);
      remoteWarning =
        error.message.includes('row-level security') || (error as { code?: string }).code === '42501'
          ? 'Saved locally. Apply migration 007 (seller catalogue RLS) in the Supabase SQL Editor to sync.'
          : error.message.includes('image_url')
            ? 'Saved locally. Apply migration 007 (image_url column) in the Supabase SQL Editor to sync.'
            : `Saved locally. Supabase sync failed: ${error.message}`;
    } else if (data) {
      product = mapDbProduct(data as DbProductRow);
      const locals = readLocalSellerProducts().filter((p) => p.id !== product.id);
      writeLocalSellerProducts(locals);
    }
  }

  await refreshCatalog({ includeInactiveForFactory: factory });
  const saved = productById(product.id) || product;
  const warning = [imageWarning, remoteWarning].filter(Boolean).join(' ');
  if (warning) {
    (saved as CatalogProduct & { syncWarning?: string }).syncWarning = warning;
  }
  return saved;
}

export function productImageHtml(
  product: Pick<CatalogProduct, 'imageUrl' | 'icon' | 'name' | 'category'>,
  opts: { className?: string; size?: 'card' | 'detail' | 'thumb' } = {},
): string {
  const cls = opts.className || 'product-image';
  const size = opts.size || 'card';
  if (product.imageUrl) {
    return `<img class="${cls} ${cls}--${size}" src="${escapeAttr(product.imageUrl)}" alt="" loading="lazy" decoding="async" />`;
  }
  return `<div class="${cls} ${cls}--${size} ${cls}--placeholder" data-category="${escapeAttr(product.category)}" aria-hidden="true"><span class="product-image-icon">${product.icon || '📦'}</span></div>`;
}

function escapeAttr(str: unknown): string {
  return String(str ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
